<script>
import Layout from "@/components/layout/app-layout";
import appConfig from "@/app.config";
import PageHeader from "@/components/layout/page-header";
import moment from "moment";

// Import reporting components
import ReportingRevenueCard from "./shared/reporting-revenue-card";
import ReportingQuantityCard from "./shared/reporting-quantity-card";
import ReportingTimeseriesChart from "./shared/reporting-timeseries-chart";
import ReportingCustomerChart from "./shared/reporting-customer-chart";
import ReportingPieChart from "./shared/reporting-pie-chart";
import ReportingDataTable from "./shared/reporting-data-table";

// Import mock data
import { mockCustomers, mockCategories } from "./shared/reporting-mock-data";

export default {
  name: "stats.reporting",
  page: {
    title: "Sales Order Reporting",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    ReportingRevenueCard,
    ReportingQuantityCard,
    ReportingTimeseriesChart,
    ReportingCustomerChart,
    ReportingPieChart,
    ReportingDataTable,
  },
  data() {
    return {
      isLoading: false,
      items: [
        {
          text: "Statistics",
        },
        {
          text: "Reporting",
          active: true,
        },
      ],

      // Period selection
      periodType: "month",
      periodOptions: [
        { value: "week", text: "Weekly" },
        { value: "month", text: "Monthly" },
        { value: "year", text: "Yearly" },
      ],

      // Filters
      filters: {
        customerId: null,
        categoryId: null,
        dateRange: {
          startDate: moment()
            .startOf("year")
            .format("YYYY-MM-DD"),
          endDate: moment()
            .endOf("year")
            .format("YYYY-MM-DD"),
        },
      },

      // Filter options
      customers: mockCustomers,
      categories: mockCategories,

      // Date range picker config
      dateRangeConfig: {
        mode: "range",
        maxDate: moment().format("YYYY-MM-DD"),
        defaultDate: [
          moment()
            .startOf("year")
            .format("YYYY-MM-DD"),
          moment()
            .endOf("year")
            .format("YYYY-MM-DD"),
        ],
      },

      // UI state
      showFilters: true,
    };
  },
  computed: {
    hasActiveFilters() {
      return (
        this.filters.customerId !== null || this.filters.categoryId !== null
      );
    },
    periodLabel() {
      const labels = {
        week: "Weekly",
        month: "Monthly",
        year: "Yearly",
      };
      return labels[this.periodType];
    },
  },
  methods: {
    async applyFilters() {
      this.isLoading = true;

      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // In production, this would trigger API calls to fetch filtered data
      // For now, the components use mock data

      this.isLoading = false;

      this.$bvToast.toast("Filters applied successfully", {
        title: "Success",
        variant: "success",
        solid: true,
        autoHideDelay: 3000,
      });
    },
    clearFilters() {
      this.filters = {
        customerId: null,
        categoryId: null,
        dateRange: {
          startDate: moment()
            .startOf("year")
            .format("YYYY-MM-DD"),
          endDate: moment()
            .endOf("year")
            .format("YYYY-MM-DD"),
        },
      };
      this.applyFilters();
    },
    async refreshData() {
      this.isLoading = true;

      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 800));

      // Trigger refresh on all components
      // In production, this would refetch data from the API

      this.isLoading = false;

      this.$bvToast.toast("Data refreshed", {
        title: "Success",
        variant: "info",
        solid: true,
        autoHideDelay: 2000,
      });
    },
    changePeriod(period) {
      this.periodType = period;
    },
    toggleFilters() {
      this.showFilters = !this.showFilters;
    },
    exportData() {
      // Placeholder for export functionality
      this.$bvToast.toast("Export functionality coming soon", {
        title: "Info",
        variant: "info",
        solid: true,
        autoHideDelay: 3000,
      });
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :items="items">
      <div
        slot="title"
        class="d-flex align-items-center justify-content-between w-100"
      >
        <div class="d-flex align-items-center">
          <h4 class="mb-0 me-3">{{ $t("Sales Order Reporting") }}</h4>
          <b-button variant="icon" @click="refreshData" :disabled="isLoading">
            <i
              class="bx bx-sync font-size-18"
              :class="{ 'bx-spin': isLoading }"
            ></i>
          </b-button>
        </div>
        <div class="d-flex align-items-center">
          <b-button
            variant="light"
            size="sm"
            @click="toggleFilters"
            class="me-2"
          >
            <i class="bx bx-filter-alt me-1"></i>
            {{ showFilters ? "Hide" : "Show" }} Filters
          </b-button>
          <b-button variant="primary" size="sm" @click="exportData">
            <i class="bx bx-download me-1"></i>
            Export
          </b-button>
        </div>
      </div>
    </PageHeader>

    <!-- Period Type Selector -->
    <b-row class="mb-3">
      <b-col>
        <b-card no-body class="shadow-sm">
          <b-card-body class="py-3">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h5 class="mb-1">View by Period</h5>
                <p class="text-muted mb-0 small">
                  Select the time period for analysis
                </p>
              </div>
              <b-button-group>
                <b-button
                  v-for="option in periodOptions"
                  :key="option.value"
                  :variant="
                    periodType === option.value ? 'primary' : 'outline-primary'
                  "
                  @click="changePeriod(option.value)"
                >
                  <i class="bx bx-calendar me-1"></i>
                  {{ option.text }}
                </b-button>
              </b-button-group>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Filters Section -->
    <b-collapse v-model="showFilters" class="mb-3">
      <b-card no-body class="shadow-sm">
        <b-card-body>
          <h5 class="mb-3">
            <i class="bx bx-filter text-primary me-2"></i>
            Filters
          </h5>
          <b-row>
            <!-- Customer Filter -->
            <b-col md="4">
              <b-form-group label="Customer" label-for="filter-customer">
                <b-form-select
                  id="filter-customer"
                  v-model="filters.customerId"
                  :options="[
                    { value: null, text: 'All Customers' },
                    ...customers.map((c) => ({ value: c.id, text: c.name })),
                  ]"
                ></b-form-select>
              </b-form-group>
            </b-col>

            <!-- Category Filter -->
            <b-col md="4">
              <b-form-group
                label="Product Category"
                label-for="filter-category"
              >
                <b-form-select
                  id="filter-category"
                  v-model="filters.categoryId"
                  :options="[
                    { value: null, text: 'All Categories' },
                    ...categories.map((c) => ({ value: c.id, text: c.name })),
                  ]"
                ></b-form-select>
              </b-form-group>
            </b-col>

            <!-- Date Range (Placeholder for future date picker) -->
            <b-col md="4">
              <b-form-group label="Date Range" label-for="filter-date">
                <b-input-group>
                  <b-form-input
                    id="filter-date"
                    :value="
                      `${filters.dateRange.startDate} to ${filters.dateRange.endDate}`
                    "
                    readonly
                    placeholder="Select date range"
                  ></b-form-input>
                  <b-input-group-append>
                    <b-button variant="outline-secondary">
                      <i class="bx bx-calendar"></i>
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Filter Actions -->
          <b-row>
            <b-col class="d-flex justify-content-end">
              <b-button
                variant="light"
                class="me-2"
                @click="clearFilters"
                :disabled="!hasActiveFilters"
              >
                <i class="bx bx-x me-1"></i>
                Clear Filters
              </b-button>
              <b-button
                variant="primary"
                @click="applyFilters"
                :disabled="isLoading"
              >
                <i class="bx bx-check me-1"></i>
                Apply Filters
              </b-button>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>
    </b-collapse>

    <!-- Key Metrics Cards -->
    <b-row class="mb-3">
      <b-col md="6">
        <reporting-revenue-card
          :period-type="periodType"
          :is-loading="isLoading"
          class="shadow-sm"
        />
      </b-col>
      <b-col md="6">
        <reporting-quantity-card
          :period-type="periodType"
          :is-loading="isLoading"
          class="shadow-sm"
        />
      </b-col>
    </b-row>

    <!-- Charts Section -->
    <b-row class="mb-3">
      <b-col md="12">
        <reporting-timeseries-chart
          :period-type="periodType"
          :is-loading="isLoading"
          class="shadow-sm"
        />
      </b-col>
    </b-row>

    <b-row class="mb-3">
      <b-col md="6">
        <reporting-customer-chart
          :period-type="periodType"
          :is-loading="isLoading"
          class="shadow-sm"
        />
      </b-col>
      <b-col md="6">
        <reporting-pie-chart
          :period-type="periodType"
          :is-loading="isLoading"
          class="shadow-sm"
        />
      </b-col>
    </b-row>

    <!-- Data Table -->
    <b-row>
      <b-col md="12">
        <reporting-data-table
          :period-type="periodType"
          :is-loading="isLoading"
          class="shadow-sm"
        />
      </b-col>
    </b-row>
  </Layout>
</template>

<style scoped>
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.bx-spin {
  animation: spin 1s linear infinite;
}

::v-deep .card {
  border: none;
  margin-bottom: 24px;
}

::v-deep .card-body {
  padding: 1.5rem;
}

::v-deep .shadow-sm {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
}

.btn-group .btn {
  padding: 0.5rem 1rem;
}

.btn-group .btn i {
  font-size: 16px;
}
</style>
